import {
  buildDepartments,
  buildUsers,
  normalizeBackendSkill,
  normalizeSession,
  normalizeTaskStatus,
  sampleSkills,
  taskMetadata,
} from "./data";
import type {
  AgentStartResponse,
  AgenticSkill,
  BackendSkillRequest,
  BackendSkillsResponse,
  ChatState,
  FleetAgentSession,
  SessionSnapshot,
  SkillDepartment,
  SkillTaskMetadata,
  SkillTaskStatus,
  SkillUser,
} from "./types";

const productionBaseURL = "https://the-construct-skill-backend.andygruening.workers.dev";

export const apiBaseURL = import.meta.env.VITE_CONSTRUCT_BACKEND_URL || productionBaseURL;

export interface MarketplaceData {
  skills: AgenticSkill[];
  users: SkillUser[];
  departments: SkillDepartment[];
}

export async function fetchMarketplace(): Promise<MarketplaceData> {
  const response = await requestJSON<BackendSkillsResponse>("/api/skills");
  const skills = (response.skills ?? []).map(normalizeBackendSkill);
  const resolvedSkills = skills.length > 0 ? skills : sampleSkills();

  return {
    skills: resolvedSkills,
    users: buildUsers(resolvedSkills),
    departments: buildDepartments(resolvedSkills),
  };
}

export async function reserveSkillRequestUID(): Promise<string> {
  const response = await requestJSON<{ uid: string }>("/api/skills/requests/uid", { method: "POST" });
  return response.uid;
}

export async function fetchSessionSnapshot(state: ChatState): Promise<SessionSnapshot> {
  const sessions = await fetchSessions(state);
  const taskStatuses: Record<string, SkillTaskStatus> = {};
  const taskMetadataBySession: Record<string, SkillTaskMetadata> = {};
  const normalizedSessions = [];

  for (const session of sessions) {
    if (state === "create_skill") {
      const sessionID = session.id ?? session.sessionId ?? session.session_id ?? session.runId;
      const request = sessionID ? await fetchSessionRequest(String(sessionID)).catch(() => undefined) : undefined;
      const status = normalizeTaskStatus(request, session.status ?? session.state);
      const metadata = taskMetadata(request);
      const title = `${displayRequestName(request, session)} (${shortTaskStatus(status)})`;
      const normalized = normalizeSession(session, title);
      taskStatuses[normalized.id] = status;
      taskMetadataBySession[normalized.id] = metadata;
      normalizedSessions.push(normalized);
    } else {
      normalizedSessions.push(normalizeSession(session));
    }
  }

  return {
    sessions: normalizedSessions,
    taskStatuses,
    taskMetadata: taskMetadataBySession,
    isLoading: false,
    hasLoaded: true,
  };
}

export async function fetchSessionDetail(state: ChatState, sessionID: string): Promise<SessionSnapshot> {
  const response = await requestJSON<unknown>(
    `${sessionPath(state)}/${encodeURIComponent(sessionID)}${state === "brainstorm" ? "" : `?state=${state}`}`,
  );
  const resolvedSession = unwrapSessionResponse(response);
  const taskStatuses: Record<string, SkillTaskStatus> = {};
  const taskMetadataBySession: Record<string, SkillTaskMetadata> = {};

  if (!resolvedSession) {
    return { sessions: [], taskStatuses, taskMetadata: taskMetadataBySession, isLoading: false, hasLoaded: true };
  }

  const normalized = normalizeSession(resolvedSession);
  if (state === "create_skill") {
    const request = await fetchSessionRequest(normalized.id).catch(() => undefined);
    taskStatuses[normalized.id] = normalizeTaskStatus(request, resolvedSession.status ?? resolvedSession.state);
    taskMetadataBySession[normalized.id] = taskMetadata(request);
  }

  return {
    sessions: [normalized],
    taskStatuses,
    taskMetadata: taskMetadataBySession,
    isLoading: false,
    hasLoaded: true,
  };
}

export async function sendAgentMessage(params: {
  state: ChatState;
  message: string;
  sessionId?: string;
  skillID?: string;
  skillName?: string;
  uid?: string;
}): Promise<AgentStartResponse> {
  const body =
    params.state === "brainstorm"
      ? { message: params.message, sessionId: params.sessionId }
      : {
          state: params.state,
          message: params.message,
          sessionId: params.sessionId,
          skillID: params.skillID,
          skillName: params.skillName,
          uid: params.uid,
        };

  return requestJSON<AgentStartResponse>(sessionPath(params.state), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function shareURL(skillID: string): string {
  const url = new URL("/skills", productionBaseURL);
  url.searchParams.set("skill", skillID);
  return url.toString();
}

async function fetchSessions(state: ChatState): Promise<FleetAgentSession[]> {
  const response = await requestJSON<FleetAgentSession[] | { sessions?: FleetAgentSession[]; data?: FleetAgentSession[]; items?: FleetAgentSession[] }>(
    `${sessionPath(state)}${state === "brainstorm" ? "" : `?state=${state}`}`,
  );

  if (Array.isArray(response)) return response;
  return response.sessions ?? response.data ?? response.items ?? [];
}

async function fetchSessionRequest(sessionID: string): Promise<BackendSkillRequest | undefined> {
  const response = await requestJSON<{ uid?: string; request?: BackendSkillRequest }>(
    `/api/architect/sessions/${encodeURIComponent(sessionID)}/request`,
  );
  if (!response.request) return undefined;
  return { ...response.request, uid: response.request.uid ?? response.uid };
}

function sessionPath(state: ChatState): string {
  return state === "brainstorm" ? "/api/morpheus/sessions" : "/api/architect/sessions";
}

function unwrapSessionResponse(response: unknown): FleetAgentSession | undefined {
  if (!response || typeof response !== "object") return undefined;
  const object = response as Record<string, unknown>;
  const session = (object.session ?? object.data ?? object.item ?? object.result ?? object) as FleetAgentSession;
  const events = Array.isArray(object.events) ? (object.events as FleetAgentSession["events"]) : undefined;
  const messages = Array.isArray(object.messages) ? (object.messages as FleetAgentSession["messages"]) : undefined;
  return {
    ...session,
    events: session.events ?? events,
    messages: session.messages ?? messages,
  };
}

async function requestJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith("http") ? path : `${apiBaseURL}${path}`;
  const response = await fetch(url, init);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(readableError(text) || "The request could not be completed.");
  }

  return (text ? JSON.parse(text) : {}) as T;
}

function readableError(text: string): string {
  if (!text) return "";

  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string };
    return parsed.error || parsed.message || "";
  } catch {
    return text;
  }
}

function displayRequestName(request: BackendSkillRequest | undefined, session: FleetAgentSession): string {
  return request?.skill?.title || request?.skill?.name || request?.skillID || request?.uid || session.title || session.id || "Skill request";
}

function shortTaskStatus(status: SkillTaskStatus): string {
  switch (status.kind) {
    case "waiting":
      return "Waiting";
    case "running":
      return "Running";
    case "in_review":
      return "In review";
    case "approved":
      return "Approved";
    case "declined":
      return "Declined";
  }
}
